import React from "react";
import {
  Box,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  down_sort_arrow_icon,
  edit_active_icon,
  edit_disabled_icon,
  review_disabled_icon,
  review_icon,
  up_sort_arrow_icon,
  assessment_evaluation_reset_default_icon,
  assessment_evaluation_reset_disabled_icon,
} from "../../constants/icons";
import IconButtons from "../IconButtons/IconButtons";
import TablePagination from "../TablePagination/TablePagination";
import {
  evaluationStatusChip,
  font_13_icon,
  font_9,
  getStatus,
  scoreChip,
} from "../../utils/utils";
import {
  sortInAscending,
  sortInDescending,
  AttendeeStatus,
  HelpTexts,
} from "../../constants/constants";
import dayjs from "dayjs";
import UserTableAvatar from "../UserTableAvatar/UserTableAvatar";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
const { DROPPED, SEPARATED } = AttendeeStatus;
const { NON_ACTIVE_ATTENDEE } = HelpTexts;
const tz = "Asia/Kolkata";

// Helper function to get sort icon size
const getSortIconSize = (isActive) => (isActive ? null : font_9);

// Component for Sortable Column Header
const SortableColumnHeader = ({
  label,
  sortKey,
  sortBy,
  orderBy,
  handleSorting,
  minWidth,
  align = "left",
}) => {
  const isAscActive = sortBy === sortKey && orderBy === "ASC";
  const isDescActive = sortBy === sortKey && orderBy === "DESC";
  const showDescIcon =
    sortKey !== "attendeeName" || sortBy !== sortKey || orderBy !== "DESC";

  return (
    <TableCell align={align} sx={{ minWidth }} className="padding-left-1">
      <Typography variant="font_13_bold" className="flex-table-sort-head">
        <Box className="table-sort-head">{label}</Box>
        <Box className="table-sort-icon">
          <IconButtons
            height={getSortIconSize(isAscActive)}
            width={getSortIconSize(isAscActive)}
            image={up_sort_arrow_icon}
            handleClick={() => handleSorting("ASC", sortKey)}
            disabled={isAscActive}
            tooltip={sortInAscending}
          />
          {showDescIcon && (
            <IconButtons
              height={getSortIconSize(isDescActive)}
              width={getSortIconSize(isDescActive)}
              image={down_sort_arrow_icon}
              handleClick={() => handleSorting("DESC", sortKey)}
              disabled={isDescActive}
              tooltip={sortInDescending}
            />
          )}
        </Box>
      </Typography>
    </TableCell>
  );
};

// Helper function to get attendee status
const getAttendeeStatus = (evaluation) => {
  if (evaluation?.bootcampAttendee != null) {
    const status = evaluation?.bootcampAttendee?.status;
    if (status === DROPPED || status === SEPARATED) {
      return status;
    }
    return null;
  }

  const moduleAttendeeStatus = evaluation?.moduleAttendee?.status;
  if (moduleAttendeeStatus === DROPPED || moduleAttendeeStatus === SEPARATED) {
    return moduleAttendeeStatus;
  }

  const attendeeStatus = evaluation?.moduleAttendee?.attendee?.status;
  if (attendeeStatus === DROPPED || attendeeStatus === SEPARATED) {
    return attendeeStatus;
  }

  return null;
};

// Helper function to get status updated date
const getStatusUpdatedDate = (evaluation) => {
  if (evaluation?.bootcampAttendee != null) {
    const status = evaluation?.bootcampAttendee?.status;
    if (status === DROPPED || status === SEPARATED) {
      return evaluation?.bootcampAttendee?.statusUpdatedDate;
    }
    return null;
  }

  const moduleAttendeeStatus = evaluation?.moduleAttendee?.status;
  if (moduleAttendeeStatus === DROPPED || moduleAttendeeStatus === SEPARATED) {
    return evaluation?.moduleAttendee?.statusUpdatedDate;
  }

  const attendeeStatus = evaluation?.moduleAttendee?.attendee?.status;
  if (attendeeStatus === DROPPED || attendeeStatus === SEPARATED) {
    return evaluation?.moduleAttendee?.attendee?.statusUpdatedDate;
  }

  return null;
};

// Helper function to check if user is dropped
const isUserDropped = (evaluation) => {
  if (evaluation?.bootcampAttendee !== null) {
    return evaluation?.bootcampAttendee?.status === DROPPED;
  }
  return (
    evaluation?.moduleAttendee?.attendee?.status === DROPPED ||
    evaluation?.moduleAttendee?.status === DROPPED
  );
};

// Helper function to check if user is separated
const isUserSeparated = (evaluation) => {
  if (evaluation?.bootcampAttendee !== null) {
    return evaluation?.bootcampAttendee?.status === SEPARATED;
  }
  return (
    evaluation?.moduleAttendee?.attendee?.status === SEPARATED ||
    evaluation?.moduleAttendee?.status === SEPARATED
  );
};

// Helper function to get attendee tooltip
const getAttendeeTooltip = (
  userDropped,
  userSeperated,
  attendeeStatuses,
  attendeeStatus,
  statusUpdatedDate,
  tz
) => {
  if (userDropped || userSeperated) {
    return NON_ACTIVE_ATTENDEE(
      getStatus(attendeeStatuses, attendeeStatus),
      dayjs(statusUpdatedDate)?.tz(tz)?.format("DD MMM YYYY")
    );
  }
  return null;
};

// Component for HR columns
const HrColumns = ({ bootcamp, attendee }) => {
  return (
    <>
      <TableCell align="left" className="padding-left-1">
        <BootstrapTooltip title="View Bootcamp">
          <Typography variant="font_12" color="secondary">
            <Link
              to={`/`}
              state={{
                propsToPass: {
                  bootcampName: bootcamp?.name,
                  bootcampId: bootcamp?.id,
                },
              }}
            >
              <u>{bootcamp?.name}</u>
            </Link>
          </Typography>
        </BootstrapTooltip>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">{attendee?.competency?.name}</Typography>
      </TableCell>
    </>
  );
};

// Component for Interview columns
const InterviewColumns = ({ leadInterviewer, coInterviewer }) => {
  return (
    <>
      <TableCell align="left" className="padding-left-1">
        {!!leadInterviewer ? (
          <UserTableAvatar
            imgAlt={leadInterviewer?.fullName}
            imgSrc={leadInterviewer?.profilePictureUrl}
            href={leadInterviewer?.employeeCode}
            label={leadInterviewer?.fullName}
            subLabel={leadInterviewer?.email}
            leadInterviewer={leadInterviewer?.deleted}
          />
        ) : (
          "-"
        )}
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        {!!coInterviewer ? (
          <UserTableAvatar
            imgAlt={coInterviewer?.fullName}
            imgSrc={coInterviewer?.profilePictureUrl}
            href={coInterviewer?.employeeCode}
            label={coInterviewer?.fullName}
            subLabel={coInterviewer?.email}
            deleted={coInterviewer?.deleted}
          />
        ) : (
          "-"
        )}
      </TableCell>
    </>
  );
};

// Component for Action buttons
const ActionButtons = ({
  evaluationId,
  evaluation,
  isTypeInterview,
  canViewEditAssessmentEvaluation,
  canEditAssessmentEvaluation,
  editEvaluationMessage,
  canViewResetAssessmentEvaluation,
  canResetAssessmentEvaluation,
  resetEvaluationMessage,
  handleViewEvaluation,
  handleEditEvaluation,
  handleResetEvaluations,
}) => {
  return (
    <Box>
      <IconButtons
        height={font_13_icon}
        width={font_13_icon}
        image={review_icon}
        classList="margin-right-1"
        handleClick={() => handleViewEvaluation(evaluationId)}
        tooltip="View"
        disabledImage={review_disabled_icon}
      />
      {isTypeInterview && canViewEditAssessmentEvaluation && (
        <IconButtons
          tooltip="Edit Evaluation"
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          handleClick={() => handleEditEvaluation(evaluation)}
          classList="margin-right-1"
          disabled={!canEditAssessmentEvaluation}
          disabledImage={edit_disabled_icon}
          disabledTooltip={
            !canEditAssessmentEvaluation ? editEvaluationMessage : null
          }
        />
      )}
      {canViewResetAssessmentEvaluation && (
        <IconButtons
          tooltip="Reset Evaluation"
          width={font_13_icon}
          height={font_13_icon}
          image={assessment_evaluation_reset_default_icon}
          handleClick={() => handleResetEvaluations([evaluation?.id])}
          classList="margin-right-1"
          disabled={!canResetAssessmentEvaluation}
          disabledImage={assessment_evaluation_reset_disabled_icon}
          disabledTooltip={
            !canResetAssessmentEvaluation ? resetEvaluationMessage : null
          }
        />
      )}
    </Box>
  );
};

// Component for Evaluation Table Row
const EvaluationTableRow = ({
  evaluation,
  isHr,
  isTypeInterview,
  attendeeStatuses,
  handleViewEvaluation,
  handleEditEvaluation,
  handleResetEvaluations,
  tz,
}) => {
  const evaluationId = evaluation?.id;
  const leadInterviewer = evaluation?.leadInterviewer;
  const coInterviewer = evaluation?.coInterviewer;
  const marksObtained = evaluation?.marksObtained;
  const totalMarks = evaluation?.totalMarks;
  const startDate = evaluation?.startDate;
  const submissionDate = evaluation?.endDate;
  const attendee =
    evaluation?.bootcampAttendee?.attendee ||
    evaluation?.moduleAttendee?.attendee?.attendee;
  const bootcamp =
    evaluation?.bootcampAttendee?.bootcamp ||
    evaluation?.moduleAttendee?.attendee?.bootcamp;
  const status = evaluationStatusChip(evaluation?.status);
  const permissions = evaluation?.permission;
  const {
    canViewEditAssessmentEvaluation,
    canEditAssessmentEvaluation,
    editEvaluationMessage,
    canViewResetAssessmentEvaluation,
    canResetAssessmentEvaluation,
    resetEvaluationMessage,
  } = permissions || {};

  const userDropped = isUserDropped(evaluation);
  const userSeperated = isUserSeparated(evaluation);
  const attendeeStatus = getAttendeeStatus(evaluation);
  const statusUpdatedDate = getStatusUpdatedDate(evaluation);
  const isActiveOrCompleted =
    evaluation?.status === "ACTIVE" || evaluation?.status === "COMPLETED";
  const attendeeTooltip = getAttendeeTooltip(
    userDropped,
    userSeperated,
    attendeeStatuses,
    attendeeStatus,
    statusUpdatedDate,
    tz
  );

  return (
    <TableRow className="table-body-row" hover key={evaluationId}>
      <TableCell align="left" className="padding-left-1">
        <UserTableAvatar
          imgAlt={attendee?.fullName}
          imgSrc={attendee?.profilePictureUrl}
          href={attendee?.employeeCode}
          label={attendee?.fullName}
          subLabel={attendee?.email}
          deleted={userDropped || userSeperated}
          tooltip={attendeeTooltip}
        />
      </TableCell>
      {isHr && <HrColumns bootcamp={bootcamp} attendee={attendee} />}
      {isTypeInterview && (
        <InterviewColumns
          leadInterviewer={leadInterviewer}
          coInterviewer={coInterviewer}
        />
      )}
      <TableCell align="left" className="padding-left-1">
        <Chip
          className="score-chip"
          label={`${marksObtained || 0}/${totalMarks}`}
          variant={scoreChip(
            marksObtained,
            totalMarks,
            null,
            !isActiveOrCompleted
          )}
        />
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          <Chip
            className="same-size-chip"
            label={status?.name}
            variant={status?.theme}
          />
        </Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          {startDate ? dayjs(startDate)?.format("DD/MM/YYYY hh:mm A") : "-"}
        </Typography>
      </TableCell>
      <TableCell align="left" className="padding-left-1">
        <Typography variant="font_12">
          {submissionDate
            ? dayjs(submissionDate)?.format("DD/MM/YYYY hh:mm A")
            : "-"}
        </Typography>
      </TableCell>
      <TableCell className="padding-left-1 padding-right-1">
        <ActionButtons
          evaluationId={evaluationId}
          evaluation={evaluation}
          isTypeInterview={isTypeInterview}
          canViewEditAssessmentEvaluation={canViewEditAssessmentEvaluation}
          canEditAssessmentEvaluation={canEditAssessmentEvaluation}
          editEvaluationMessage={editEvaluationMessage}
          canViewResetAssessmentEvaluation={canViewResetAssessmentEvaluation}
          canResetAssessmentEvaluation={canResetAssessmentEvaluation}
          resetEvaluationMessage={resetEvaluationMessage}
          handleViewEvaluation={handleViewEvaluation}
          handleEditEvaluation={handleEditEvaluation}
          handleResetEvaluations={handleResetEvaluations}
        />
      </TableCell>
    </TableRow>
  );
};

const EvaluationListingTable = ({
  evaluationListing,
  totalPages,
  handlePageChange,
  currentPage,
  handleSorting,
  sortBy,
  orderBy,
  totalResult,
  handleEditEvaluation,
  handleViewEvaluation,
  handleReopenEvaluationModal,
  isTypeInterview,
  isHr,
  handleResetEvaluations,
}) => {
  const attendeeStatuses = useSelector(
    (state) => state?.utility?.constants?.attendeeStatuses
  );
  return (
    <>
      <TableContainer className="table-container">
        <Table stickyHeader>
          <TableHead className="table-head-container">
            <TableRow className="table-head-row" sx={{ height: "2.711rem" }}>
              <SortableColumnHeader
                label="Attendee Name/Email"
                sortKey="attendeeName"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="13rem"
              />
              {isHr && (
                <>
                  <SortableColumnHeader
                    label="Bootcamp"
                    sortKey="attendeeBootcamp"
                    sortBy={sortBy}
                    orderBy={orderBy}
                    handleSorting={handleSorting}
                    minWidth="13rem"
                  />
                  <SortableColumnHeader
                    label="Competency"
                    sortKey="attendeeCompetency"
                    sortBy={sortBy}
                    orderBy={orderBy}
                    handleSorting={handleSorting}
                    minWidth="9rem"
                  />
                </>
              )}
              {isTypeInterview && (
                <>
                  <TableCell
                    align="left"
                    sx={{ minWidth: "13rem" }}
                    className="padding-left-1"
                  >
                    <Typography
                      variant="font_13_bold"
                      className="flex-table-sort-head"
                    >
                      <Box className="table-sort-head">Lead Interviewer</Box>
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ minWidth: "13rem" }}
                    className="padding-left-1"
                  >
                    <Typography
                      variant="font_13_bold"
                      className="flex-table-sort-head"
                    >
                      <Box className="table-sort-head">Co Interviewer</Box>
                    </Typography>
                  </TableCell>
                </>
              )}
              <SortableColumnHeader
                label="Marks"
                sortKey="marksObtained"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <SortableColumnHeader
                label="Status"
                sortKey="status"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <SortableColumnHeader
                label="Start Date"
                sortKey="startDate"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <SortableColumnHeader
                label="Submission Date"
                sortKey="endDate"
                sortBy={sortBy}
                orderBy={orderBy}
                handleSorting={handleSorting}
                minWidth="9rem"
              />
              <TableCell
                sx={{ minWidth: "9rem", maxWidth: "9rem", width: "9rem" }}
                className="padding-left-1 padding-right-1"
              >
                <Typography variant="font_13_bold">Actions</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {evaluationListing?.map((evaluation) => (
              <EvaluationTableRow
                key={evaluation?.id}
                evaluation={evaluation}
                isHr={isHr}
                isTypeInterview={isTypeInterview}
                attendeeStatuses={attendeeStatuses}
                handleViewEvaluation={handleViewEvaluation}
                handleEditEvaluation={handleEditEvaluation}
                handleResetEvaluations={handleResetEvaluations}
                tz={tz}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container className="table-pagination-container">
        <Typography variant="font_16">Total {totalResult} Records</Typography>
        <TablePagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          currentPage={currentPage}
        />
      </Grid>
    </>
  );
};

export default EvaluationListingTable;
